import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Icon,
  Switch,
  Tooltip,
  SentrilockValidator,
  MasterlockValidator,
  SupraLockValidator,
} from 'components';
import _, { cloneDeep } from 'lodash';
import { ConfirmationInstructionsJsonType } from '@bb/domain/dist/types/AppointmentConfig';
import { IMLSNumbers, isTeamAdmin } from '@bb/users';
import { useIsFirstRender } from 'hooks';
import {
  AuthContextType,
  AuthContextUser,
  ListingLabelContextProvider,
} from 'contexts';
import { SupraSystemSubscriptionStatus } from '@bb/domain/dist/types';
import { featureEnabled } from 'utils';
import { OverlayTrigger, Tooltip as BootstrapTooltip } from 'react-bootstrap';
import { Text } from 'components/Typography/Text';

export type ShowingAgentInstructionsProps = {
  accessesMap: { [x: string]: string };
  appointmentConfig: any;
  otherAccessOptions: { $$hashKey: string; id: string; label: string }[];
  supraEkeyAccessOptions: { $$hashKey: string; id: string; label: string }[];
  supraLockBoxAccessOptions: { $$hashKey: string; id: string; label: string }[];
  sentriLockAccessOptions: { $$hashKey: string; id: string; label: string }[];
  masterLockAccessOptions: { $$hashKey: string; id: string; label: string }[];
  lockboxPhoto: any;
  showAlarmInstruction: boolean;
  openFileUpload: () => void;
  areAppointmentInstructionsInvalid: boolean;
  setShowingAgentInstructions: (showingAgentInstructions: any) => void;
  changeDisplayToSellerToggle: (displayedToSeller: boolean) => void;
  // sentriLock Validator
  setGetLockboxesFalse: () => void;
  shouldGetLockboxes: boolean;
  listingId: string;
  listingReps: any[];
  listingOrgFeatures: string[];
  mlsNumbers: {
    boardId: string;
    board: string;
    number: string;
  }[];
  sentriLockInfo: Partial<ConfirmationInstructionsJsonType['sentrilockv2']>;
  setSentriLockInfo: (
    info: Partial<ConfirmationInstructionsJsonType['sentrilockv2']>,
  ) => void;
  // masterLock Validator
  resetAppointmentConfigAccess: () => void;
  setIsMasterLockCodeValid: (option: boolean) => void;
  onValidate: (deviceId?: string) => void;
  deviceId: string;
  setIsSupraLockCodeValid: (isValid: boolean) => void;
  supraLockInfo: Partial<ConfirmationInstructionsJsonType['supralockbox']>;
  setSupraLockInfo: (
    info: Partial<ConfirmationInstructionsJsonType['supralockbox']>,
  ) => void;
  setIsMasterLockOneDayCodeEnabled: (enabled: boolean) => void;
  showSupraAccessOptions?: boolean;
  showMasterLockAccessOptions?: boolean;
  showSentriLockAccessOptions?: boolean;
  showCombinationLockboxOptions?: boolean;
  $rootScope: angular.IRootScopeService & {
    auth?: AuthContextUser;
    hasFeature: AuthContextType['hasFeature'];
  };
  ToastService: any;
};

const otherAccessOptions = [
  { id: 'Direct', label: 'Direct' },
  { id: 'Key', label: 'Key' },
  { id: 'Door Code', label: 'Door Code' },
];

const combinationLockboxAccessOptions = [
  { id: 'Lock Box', label: 'Combination Lock Box' },
];

const sentriLockAccessOptions = [{ id: 'SentriLockV2', label: 'SentriLock' }];
const masterLockAccessOptions = [{ id: 'MasterLock', label: 'Master Lock' }];
const supraEkeyAccessOptions = [{ id: 'Supra eKEY', label: 'Supra eKEY' }];
const supraLockBoxAccessOptions = [{ id: 'Supra Lockbox', label: 'Supra' }];

const LockBoxBtn = ({
  lockboxPhoto,
  openFileUpload,
}: {
  lockboxPhoto: any;
  openFileUpload: () => void;
}) => (
  <div className="m-b">
    <a
      className={`btn add-lockbox-photo-button ${
        lockboxPhoto ? 'btn-warning' : 'btn-primary'
      }`}
      onClick={openFileUpload}
    >
      {lockboxPhoto ? 'Edit / Delete' : 'Add Lock Box'} Photo &nbsp;
      <i
        className={`fa ${lockboxPhoto ? 'fa-picture-o' : 'fa-camera'}`}
        aria-hidden="true"
      ></i>
    </a>
  </div>
);

const ShowingAgentInstructions = ({
  accessesMap,
  appointmentConfig,
  openFileUpload,
  areAppointmentInstructionsInvalid,
  setShowingAgentInstructions: _setShowingAgentInstructions,
  changeDisplayToSellerToggle,
  setGetLockboxesFalse,
  shouldGetLockboxes,
  listingId,
  listingReps,
  listingOrgFeatures,
  mlsNumbers,
  sentriLockInfo,
  setSentriLockInfo,
  resetAppointmentConfigAccess,
  setIsMasterLockCodeValid,
  setIsMasterLockOneDayCodeEnabled,
  onValidate,
  deviceId,
  setIsSupraLockCodeValid,
  supraLockInfo,
  setSupraLockInfo,
  showSupraAccessOptions,
  showMasterLockAccessOptions,
  showSentriLockAccessOptions,
  showCombinationLockboxOptions,
  $rootScope,
  ToastService,
}: ShowingAgentInstructionsProps) => {
  const isFirstRender = useIsFirstRender();
  const [lockboxPhoto, setLockBoxPhoto] = useState<string>(
    appointmentConfig.lockboxPhoto,
  );
  const [showingAgentInstructions, setShowingAgentInstructions] = useState<any>(
    {
      access: appointmentConfig.access,
      masterlock: {
        details: appointmentConfig.instructions?.masterlock?.details || '',
        isOneDayCodeEnabled:
          appointmentConfig.instructions?.masterlock?.isOneDayCodeEnabled,
      },
      supraekey: {
        details: appointmentConfig.instructions?.supraekey?.details || '',
      },
      supralockbox: {
        details: appointmentConfig.instructions?.supralockbox?.details || '',
      },
      lockbox: {
        code: appointmentConfig.instructions?.lockbox?.code || '',
        details: appointmentConfig.instructions?.lockbox?.details || '',
      },
      doorcode: {
        code: appointmentConfig.instructions?.doorcode?.code || '',
        details: appointmentConfig.instructions?.doorcode?.details || '',
      },
      sentrilockv2: {
        details: appointmentConfig.instructions?.sentrilockv2?.details || '',
        isOneDayCodeEnabled:
          appointmentConfig.instructions?.sentrilockv2?.isOneDayCodeEnabled,
      },
      sentrilockapp: {
        details: appointmentConfig.instructions?.sentrilockapp?.details || '',
      },
      direct: {
        details: appointmentConfig.instructions?.direct?.details || '',
      },
      key: {
        details: appointmentConfig.instructions?.key?.details || '',
      },
      alarm: {
        code: appointmentConfig.instructions?.alarm?.code || '',
        details: appointmentConfig.instructions?.alarm?.details || '',
      },
      lights: !!appointmentConfig.instructions?.lights,
      shoes: !!appointmentConfig.instructions?.shoes,
      card: !!appointmentConfig.instructions?.card,
      lock_doors: !!appointmentConfig.instructions?.lock_doors,
      knockFirst: !!appointmentConfig.instructions?.knockFirst,
      callIfLate: !!appointmentConfig.instructions?.callIfLate,
      reco: appointmentConfig.instructions.reco,
      confirmation: {
        details: appointmentConfig.instructions?.confirmation?.details || '',
      },
      preBookingInformation: appointmentConfig.preBookingInformation,
    },
  );

  const checkAlarmInstruction = () =>
    showingAgentInstructions.alarm.code ||
    showingAgentInstructions.alarm.details;

  const [showAlarmInstructions, setShowAlarminstructions] = useState(
    checkAlarmInstruction(),
  );

  const checkOtherInstruction = () =>
    !!showingAgentInstructions.confirmation.details;

  const [showOtherInstructions, setShowOtherInstructions] = useState(
    checkOtherInstruction(),
  );

  const [displayedToSeller, setDisplayedToSeller] = useState(
    Boolean(appointmentConfig.displayedToSeller),
  );

  // ENG-11207 - Hide leave card option for specific boards
  const isLeaveCardAvailable = useMemo(() => {
    if (!listingReps) return true;
    const BOARDS_WITH_LEAVE_CARD_REGULATIONS = ['Chattanooga', 'MAAR', 'KAAR'];

    const listingBoards: string[] = [];
    listingReps.forEach(rep => {
      rep.mls_numbers.forEach((mlsNumber: IMLSNumbers) => {
        listingBoards.push(mlsNumber.board);
      });
    });

    return !_.uniq(listingBoards).some(board =>
      BOARDS_WITH_LEAVE_CARD_REGULATIONS.includes(board),
    );
  }, [listingReps]);

  useEffect(() => {
    _setShowingAgentInstructions(showingAgentInstructions);
  }, [_setShowingAgentInstructions, showingAgentInstructions]);

  useEffect(() => {
    changeDisplayToSellerToggle(displayedToSeller);
  }, [changeDisplayToSellerToggle, displayedToSeller]);

  const selectAccessOption = (newAccess: string) => {
    const config = { ...showingAgentInstructions, access: newAccess };

    if (config.access === 'SentriLockV2' && !config.sentrilockv2?.details) {
      config.sentrilockv2 = {
        details:
          '**ATTN - OUT OF BOARD AGENTS** please ask LA to provide you Sentriconnect access to lockbox.',
      } as any;
    }

    setShowingAgentInstructions(config);
  };

  useEffect(() => {
    if (lockboxPhoto !== appointmentConfig.lockboxPhoto)
      setLockBoxPhoto(appointmentConfig.lockboxPhoto);
  }, [appointmentConfig.lockboxPhoto, lockboxPhoto]);

  const showSentriLockAssignmentWarning =
    showingAgentInstructions.access === 'SentriLockV2' && !mlsNumbers?.length;

  const showDisplayedToSellerToggle = useMemo(
    () => $rootScope?.auth?.id && $rootScope.auth.org.country === 'USA',
    [$rootScope],
  );

  const buildSupraAccessOption = () => {
    let _opts: any[];

    if (listingOrgFeatures.includes('SUPRA_MVP') && showSupraAccessOptions) {
      const isAdmin =
        $rootScope?.auth?.isAdmin ||
        ($rootScope?.auth && isTeamAdmin($rootScope.auth));
      const isSubscribed =
        $rootScope?.auth?.supraSubscription?.status ===
        SupraSystemSubscriptionStatus.subscribed;

      if (!isAdmin && !isSubscribed) {
        return null;
      }
      _opts = supraLockBoxAccessOptions;
    } else {
      _opts = supraEkeyAccessOptions;
    }

    return (
      <>
        <li role="separator" className="divider"></li>
        {_opts?.map(a => (
          <li
            key={a.id}
            onClick={() => selectAccessOption(a.id)}
            data-cy={`appointment-access-${a.label
              .split(' ')
              .join('-')
              .toLowerCase()}`}
          >
            <a>{a.label}</a>
          </li>
        ))}
      </>
    );
  };

  const buildCominationLockboxOption = () => (
    <>
      <li role="separator" className="divider"></li>
      {combinationLockboxAccessOptions.map(a => (
        <li
          key={a.id}
          onClick={() => selectAccessOption(a.id)}
          data-cy={`appointment-access-${a.label
            .split(' ')
            .join('-')
            .toLowerCase()}`}
        >
          <a>{a.label}</a>
        </li>
      ))}
    </>
  );

  return (
    <div className="form-group col-md-7 col-lg-6 m-t-md p-l">
      <legend className="p-b-xs display-flex">
        Showing Agent Instructions
        {Boolean(showDisplayedToSellerToggle) && (
          <StyledDisplayToSellerContainer>
            <span>Displayed to seller</span>
            <Tooltip title="Turning this toggle ON will display showing agent information on showing notifications and the Seller Dashboard.">
              <StyledInfoIconContainer>
                <StyledInfoIcon type="antdInfoCircleOutlined" />
              </StyledInfoIconContainer>
            </Tooltip>
            {':'}
            <StyledSwitch
              size="small"
              checked={displayedToSeller}
              onChange={checked => setDisplayedToSeller(checked)}
            />
          </StyledDisplayToSellerContainer>
        )}
      </legend>
      <div className="row">
        <ListingLabelContextProvider authFromAngular={$rootScope.auth}>
          <div className="form-group col-sm-7 col-md-12 col-lg-7 p-x-lg">
            {featureEnabled('preBookingNotificationFeature') && (
              <>
                {listingOrgFeatures?.includes('PRE_BOOKING') && (
                  <>
                    <div>
                      <Text strong fontSize={15}>
                        Pre-booking Information{' '}
                      </Text>
                      <OverlayTrigger
                        placement="left"
                        overlay={
                          <BootstrapTooltip id="tooltip-pre-booking">
                            Use this setting to provide additional information
                            about the listing that may impact the showing
                            agent&apos;s decision to book a showing.
                          </BootstrapTooltip>
                        }
                      >
                        <span>
                          <Icon type="antdInfoCircleOutlined" width={15} />
                        </span>
                      </OverlayTrigger>
                    </div>
                    <textarea
                      className="form-control m-t-xs m-b"
                      placeholder="Enter pre-booking information"
                      rows={2}
                      data-cy="pre-booking-information"
                      value={showingAgentInstructions.preBookingInformation}
                      onChange={e => {
                        const preBookingInformation = e.target.value;
                        setShowingAgentInstructions({
                          ...showingAgentInstructions,
                          preBookingInformation,
                        });
                      }}
                    />
                  </>
                )}
                <div>
                  <Text strong fontSize={15}>
                    Access Information{' '}
                  </Text>
                  <OverlayTrigger
                    placement="left"
                    overlay={
                      <BootstrapTooltip id="tooltip-access-information-details">
                        Use this setting to provide access instructions to be
                        shared with the showing agent once a showing is
                        confirmed.
                      </BootstrapTooltip>
                    }
                  >
                    <span>
                      <Icon type="antdInfoCircleOutlined" width={15} />
                    </span>
                  </OverlayTrigger>
                </div>
              </>
            )}
            <label className="m-b-xs attention">Access Type:</label>
            <div className="dropdown">
              <button
                className="btn btn-default btn-block dropdown-toggle"
                style={{ textAlign: 'left' }}
                type="button"
                id="access-dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                data-toggle="dropdown"
                data-cy="appointment-access"
              >
                {/* display Unassign instead of empty text label when access is unassigned */}
                {accessesMap[showingAgentInstructions.access] ||
                  ($rootScope.hasFeature('SUPRA_MVP')
                    ? 'Unassign'
                    : accessesMap[showingAgentInstructions.access])}
                <div className="pull-right" style={{ display: 'inline-block' }}>
                  <span className="caret"></span>
                </div>
              </button>
              <ul
                className="dropdown-menu"
                style={{ width: '100%' }}
                aria-labelledby="access-dropdown"
              >
                <li onClick={() => selectAccessOption('')}>
                  <a
                    style={{ height: '24px' }}
                    data-cy={'appointment-access-unassign'}
                  >
                    {$rootScope.hasFeature('SUPRA_MVP') ? 'Unassign' : ''}
                  </a>
                </li>
                <li role="separator" className="divider"></li>
                {otherAccessOptions.map(a => (
                  <li
                    key={a.id}
                    onClick={() => selectAccessOption(a.id)}
                    data-cy={`appointment-access-other-${a.label
                      .split(' ')
                      .join('-')
                      .toLowerCase()}`}
                  >
                    <a>{a.label} </a>
                  </li>
                ))}
                {buildSupraAccessOption()}
                {showSentriLockAccessOptions && (
                  <>
                    <li role="separator" className="divider"></li>
                    {sentriLockAccessOptions.map(a => (
                      <li
                        key={a.id}
                        onClick={() => selectAccessOption(a.id)}
                        data-cy={`appointment-access-${a.label
                          .split(' ')
                          .join('-')
                          .toLowerCase()}`}
                      >
                        <a>{a.label}</a>
                      </li>
                    ))}
                  </>
                )}
                {showMasterLockAccessOptions && (
                  <>
                    <li role="separator" className="divider"></li>
                    {masterLockAccessOptions.map(a => (
                      <li
                        key={a.id}
                        onClick={() => selectAccessOption(a.id)}
                        data-cy="appointment-access-masterlock"
                      >
                        <a>{a.label}</a>
                      </li>
                    ))}
                  </>
                )}
                {/* Combination lockbox should always be last on the list. MVP6-149 */}
                {showCombinationLockboxOptions &&
                  buildCominationLockboxOption()}
              </ul>
            </div>

            {showingAgentInstructions.access === 'MasterLock' && (
              <div className="m-t">
                <label className="control-label">Select Lockbox: </label>

                <MasterlockValidator
                  resetAppointmentConfigAccess={resetAppointmentConfigAccess}
                  setIsMasterLockCodeValid={setIsMasterLockCodeValid}
                  setIsMasterLockOneDayCodeEnabled={
                    setIsMasterLockOneDayCodeEnabled
                  }
                  setGetLockboxesFalse={setGetLockboxesFalse}
                  onValidate={onValidate}
                  shouldGetLockboxes={shouldGetLockboxes}
                  listingId={listingId}
                  deviceId={deviceId}
                  instructions={showingAgentInstructions.masterlock}
                />
                <LockBoxBtn
                  lockboxPhoto={lockboxPhoto}
                  openFileUpload={openFileUpload}
                />
                <textarea
                  className="form-control m-t-xs"
                  placeholder="Other Master Lock instructions"
                  rows={5}
                  value={showingAgentInstructions.masterlock.details}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.masterlock.details = e.target.value;
                    setShowingAgentInstructions({ ...temp });
                  }}
                ></textarea>
              </div>
            )}

            {showingAgentInstructions.access === 'Supra eKEY' && (
              <div className="m-t">
                <div className="m-b">
                  <LockBoxBtn
                    lockboxPhoto={lockboxPhoto}
                    openFileUpload={openFileUpload}
                  />
                </div>
                <label className="control-label">Instructions:</label>
                <textarea
                  className="form-control m-t-xs"
                  placeholder="Special access instructions"
                  rows={5}
                  data-cy="supra-ekey-special-instructions"
                  value={showingAgentInstructions.supraekey.details}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.supraekey.details = e.target.value;
                    setShowingAgentInstructions({ ...temp });
                  }}
                ></textarea>
              </div>
            )}

            {listingOrgFeatures.includes('SUPRA_MVP') &&
              showingAgentInstructions.access === 'Supra Lockbox' && (
                <div className="m-t">
                  <label className="control-label">Select Lockbox: </label>
                  <SupraLockValidator
                    setGetLockboxesFalse={setGetLockboxesFalse}
                    shouldGetLockboxes={shouldGetLockboxes}
                    listingId={listingId}
                    listingReps={listingReps}
                    setIsSupraLockCodeValid={setIsSupraLockCodeValid}
                    supraLockInfo={supraLockInfo}
                    setSupraLockInfo={setSupraLockInfo}
                    supraSubscriptionStatus={
                      $rootScope?.auth?.supraSubscription?.status
                    }
                    isAdmin={
                      $rootScope?.auth?.isAdmin ||
                      ($rootScope?.auth && isTeamAdmin($rootScope.auth))
                    }
                  />
                  <LockBoxBtn
                    lockboxPhoto={lockboxPhoto}
                    openFileUpload={openFileUpload}
                  />

                  <label className="control-label">Instructions:</label>
                  <textarea
                    className="form-control m-t-xs"
                    placeholder="Special access instructions"
                    rows={5}
                    data-cy="supra-lockbox-special-instructions"
                    value={showingAgentInstructions.supralockbox.details}
                    onChange={e => {
                      const temp = cloneDeep(showingAgentInstructions);
                      temp.supralockbox.details = e.target.value;
                      setShowingAgentInstructions(temp);
                    }}
                  ></textarea>
                </div>
              )}

            {showingAgentInstructions.access === 'Lock Box' && (
              <div className="m-t">
                <label className="control-label">Lock Box Instructions:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Combination Lock Box"
                  data-cy="lockbox-code-details"
                  value={showingAgentInstructions.lockbox.code}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.lockbox.code = e.target.value;
                    setShowingAgentInstructions({ ...temp });
                  }}
                />
                <LockBoxBtn
                  lockboxPhoto={lockboxPhoto}
                  openFileUpload={openFileUpload}
                />

                <textarea
                  className="form-control m-t-xs"
                  placeholder="Special access instructions"
                  rows={5}
                  data-cy="lockbox-other-instructions"
                  value={showingAgentInstructions.lockbox.details}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.lockbox.details = e.target.value;
                    setShowingAgentInstructions({ ...temp });
                  }}
                ></textarea>
              </div>
            )}

            {showingAgentInstructions.access === 'Door Code' && (
              <div className="m-t">
                <label className="control-label">Door Code Instructions:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Door Code"
                  data-cy="door-code-details"
                  value={showingAgentInstructions.doorcode.code}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.doorcode.code = e.target.value;
                    setShowingAgentInstructions({ ...temp });
                  }}
                />

                <textarea
                  className="form-control m-t-xs"
                  placeholder="Special access instructions"
                  rows={5}
                  data-cy="door-code-other-instructions"
                  value={showingAgentInstructions.doorcode.details}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.doorcode.details = e.target.value;
                    setShowingAgentInstructions({ ...temp });
                  }}
                ></textarea>
              </div>
            )}

            {showingAgentInstructions.access === 'SentriLockV2' && (
              <div className="m-t">
                <label className="control-label">Select Lockbox: </label>
                <SentrilockValidator
                  setGetLockboxesFalse={setGetLockboxesFalse}
                  shouldGetLockboxes={shouldGetLockboxes}
                  listingId={listingId}
                  listingReps={listingReps}
                  sentriLockInfo={sentriLockInfo}
                  setSentriLockInfo={setSentriLockInfo}
                  pendingSentriLockAssignment={
                    appointmentConfig.pendingSentriLockAssignment
                  }
                  pendingSentriLockAssignmentRetryTime={
                    appointmentConfig.pendingSentriLockAssignmentRetryTime
                  }
                  isOneDayCodeEnabled={
                    showingAgentInstructions.sentrilockv2?.isOneDayCodeEnabled
                  }
                  isFirstShowingAgentInstructionsRender={isFirstRender}
                  showSentriLockAssignmentWarning={
                    showSentriLockAssignmentWarning
                  }
                  $rootScope={$rootScope}
                  ToastService={ToastService}
                />
                <LockBoxBtn
                  lockboxPhoto={lockboxPhoto}
                  openFileUpload={openFileUpload}
                />
                <textarea
                  className="form-control m-t-xs"
                  placeholder="Other SentriLock instructions"
                  rows={5}
                  value={showingAgentInstructions.sentrilockv2.details}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.sentrilockv2.details = e.target.value;
                    setShowingAgentInstructions({ ...temp });
                  }}
                ></textarea>
              </div>
            )}

            {showingAgentInstructions.access === 'SentriLock App' && (
              <div className="m-t">
                <label className="control-label">Instructions: </label>
                <LockBoxBtn
                  lockboxPhoto={lockboxPhoto}
                  openFileUpload={openFileUpload}
                />
                <textarea
                  className="form-control m-t-xs"
                  placeholder="Other SentriLock instructions"
                  rows={5}
                  value={showingAgentInstructions.sentrilockapp.details}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.sentrilockapp.details = e.target.value;
                    setShowingAgentInstructions({ ...temp });
                  }}
                ></textarea>
              </div>
            )}

            {showingAgentInstructions.access === 'Direct' && (
              <div className="m-t">
                <label className="control-label">Instructions: </label>
                <textarea
                  className="form-control m-t-xs"
                  placeholder="Special access instructions"
                  rows={5}
                  data-cy="conf-direct-details"
                  value={showingAgentInstructions.direct.details}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.direct.details = e.target.value;
                    setShowingAgentInstructions({ ...temp });
                  }}
                ></textarea>
              </div>
            )}

            {showingAgentInstructions.access === 'Key' && (
              <div className="m-t">
                <label className="control-label">Instructions: </label>
                <textarea
                  className="form-control m-t-xs"
                  placeholder="Key access instructions"
                  rows={7}
                  data-cy="key-details"
                  value={showingAgentInstructions.key.details}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.key.details = e.target.value;
                    setShowingAgentInstructions({ ...temp });
                  }}
                ></textarea>
              </div>
            )}

            {areAppointmentInstructionsInvalid && (
              <label className="error">Details required.</label>
            )}

            {showAlarmInstructions && (
              <div className="m-t">
                <label className="control-label">Alarm Instructions:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Alarm Code"
                  data-cy="conf-alarm-code"
                  value={showingAgentInstructions.alarm.code}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.alarm.code = e.target.value;
                    setShowingAgentInstructions({ ...temp });
                  }}
                />
                <textarea
                  className="form-control m-t-sm"
                  rows={7}
                  placeholder="Other alarm instructions"
                  data-cy="conf-alarm-instructions"
                  value={showingAgentInstructions.alarm.details}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.alarm.details = e.target.value;
                    setShowingAgentInstructions({ ...temp });
                  }}
                ></textarea>
              </div>
            )}
            {!checkAlarmInstruction() && (
              <a
                className="m-t-sm add-extra-box-config"
                style={{
                  color: `${showAlarmInstructions ? '#fc5858' : '#588cfc'}`,
                }}
                onClick={() => setShowAlarminstructions(!showAlarmInstructions)}
                data-cy="conf-toggle-more-alarm"
              >
                {showAlarmInstructions ? '- Remove' : '+ Add'}
                &nbsp;Alarm
              </a>
            )}
          </div>
        </ListingLabelContextProvider>
        <div className="form-group col-sm-5 col-md-12 col-lg-5 p-x-lg m-t-md">
          <div className="row">
            <label className="col-xs-10 control-label">Turn Off Lights:</label>
            <div className="col-xs-2">
              <label className="control-label">
                <input
                  type="checkbox"
                  data-cy="conf-instructions-lights"
                  checked={showingAgentInstructions.lights}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.lights = !temp.lights;
                    setShowingAgentInstructions({ ...temp });
                  }}
                />
              </label>
            </div>
            <label className="col-xs-10 control-label">Remove Shoes:</label>
            <div className="col-xs-2">
              <label className="control-label">
                <input
                  type="checkbox"
                  data-cy="conf-instructions-shoes"
                  checked={showingAgentInstructions.shoes}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.shoes = !temp.shoes;
                    setShowingAgentInstructions({ ...temp });
                  }}
                />
              </label>
            </div>
            {isLeaveCardAvailable && (
              <>
                <label className="col-xs-10 control-label">Leave Card:</label>
                <div className="col-xs-2">
                  <label className="control-label">
                    <input
                      type="checkbox"
                      data-cy="conf-instructions-card"
                      checked={showingAgentInstructions.card}
                      onChange={e => {
                        const temp = cloneDeep(showingAgentInstructions);
                        temp.card = !temp.card;
                        setShowingAgentInstructions({ ...temp });
                      }}
                    />
                  </label>
                </div>
              </>
            )}
            <label className="col-xs-10 control-label">Lock Doors:</label>
            <div className="col-xs-2">
              <label className="control-label">
                <input
                  type="checkbox"
                  data-cy="conf-instructions-lock-doors"
                  checked={showingAgentInstructions.lock_doors}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.lock_doors = !temp.lock_doors;
                    setShowingAgentInstructions({ ...temp });
                  }}
                />
              </label>
            </div>
            <label className="col-xs-10 control-label">Knock First:</label>
            <div className="col-xs-2">
              <label className="control-label">
                <input
                  type="checkbox"
                  data-cy="conf-instructions-knock-first"
                  checked={showingAgentInstructions.knockFirst}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.knockFirst = !temp.knockFirst;
                    setShowingAgentInstructions({ ...temp });
                  }}
                />
              </label>
            </div>
            <label className="col-xs-10 control-label">
              Call if Late or Cancelling:
            </label>
            <div className="col-xs-2">
              <label className="control-label">
                <input
                  type="checkbox"
                  data-cy="conf-instructions-call-late"
                  checked={showingAgentInstructions.callIfLate}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.callIfLate = !temp.callIfLate;
                    setShowingAgentInstructions({ ...temp });
                  }}
                />
              </label>
            </div>
            <label className="col-xs-10 control-label">
              Bring Agent License:
            </label>
            <div className="col-xs-2">
              <label className="control-label">
                <input
                  type="checkbox"
                  data-cy="conf-instructions-reco"
                  checked={showingAgentInstructions.reco}
                  onChange={e => {
                    const temp = cloneDeep(showingAgentInstructions);
                    temp.reco = !temp.reco;
                    setShowingAgentInstructions({ ...temp });
                  }}
                />
              </label>
            </div>
          </div>
          {showOtherInstructions && (
            <div className="m-t">
              <label className="control-label">Other Instructions:</label>
              <textarea
                className="form-control m-t-sm"
                placeholder="Other confirmation instructions"
                rows={6}
                data-cy="conf-instructions-other"
                value={showingAgentInstructions.confirmation.details}
                onChange={e => {
                  const temp = cloneDeep(showingAgentInstructions);
                  temp.confirmation.details = e.target.value;
                  setShowingAgentInstructions({ ...temp });
                }}
              ></textarea>
            </div>
          )}

          {(!showingAgentInstructions || !checkOtherInstruction()) && (
            <a
              className="m-t-sm add-extra-box-config"
              style={{
                color: `${showOtherInstructions ? '#fc5858' : '#588cfc'}`,
              }}
              onClick={() => setShowOtherInstructions(!showOtherInstructions)}
              data-cy="toggle-other-instructions"
            >
              {showOtherInstructions ? '- Remove' : '+ Add'}
              Other
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShowingAgentInstructions;

const StyledDisplayToSellerContainer = styled.div`
  font-size 75%;
  margin-left: 35px;
  display: flex;
  align-items: center;
`;

const StyledInfoIconContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledInfoIcon = styled(Icon)`
  margin-left: 10px;
  margin-right: 6px;
`;

const StyledSwitch = styled(Switch)`
  margin-left: 10px;
`;
